import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { isHttpClientError } from "../../../lib/http/errors";
import { supplierBasicDefaultListParams, supplierKindMeta } from "../config";
import {
  createSupplier,
  disableSupplier,
  enableSupplier,
  fetchDimHousLevels,
  fetchGroundOptions,
  fetchSupplierList,
  fetchUserOptions,
  updateSupplier,
} from "../repo/supplierBasicRepo";
import {
  emptySupplierMutation,
  mapSupplierMutationError,
  normalizeSupplierFilters,
} from "../service/supplierBasicService";
import type {
  SupplierItem,
  SupplierKind,
  SupplierListFilters,
  SupplierMutation,
} from "../types";

export { emptySupplierMutation };

/** 品类供应商列表页运行时。 */
export function useSupplierBasicRuntime(kind: SupplierKind) {
  const queryClient = useQueryClient();
  const meta = supplierKindMeta[kind];
  const [draftFilters, setDraftFilters] = useState<SupplierListFilters>({});
  const [submittedFilters, setSubmittedFilters] = useState<SupplierListFilters>({});
  const [page, setPage] = useState<number>(supplierBasicDefaultListParams.page);
  const [pageSize, setPageSize] = useState<number>(
    supplierBasicDefaultListParams.pageSize,
  );
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [userOptionsQuery, setUserOptionsQuery] = useState("");
  const [groundOptionsQuery, setGroundOptionsQuery] = useState("");

  const clearMutationError = useCallback(() => {
    setMutationErrorMessage("");
  }, []);

  const listParams = useMemo(
    () => ({
      ...supplierBasicDefaultListParams,
      ...submittedFilters,
      page,
      pageSize,
    }),
    [page, pageSize, submittedFilters],
  );

  const queryRootKey = ["supply-basic-settings", kind, "list"] as const;

  const query = useQuery({
    queryKey: [...queryRootKey, listParams],
    queryFn: () => fetchSupplierList(kind, listParams),
  });

  const housLevelQuery = useQuery({
    queryKey: ["supply-basic-settings", "dim-hous-levels"],
    queryFn: fetchDimHousLevels,
    enabled: meta.showHousLevel,
  });

  const groundQuery = useQuery({
    queryKey: ["supply-basic-settings", "ground-options", groundOptionsQuery],
    queryFn: () => fetchGroundOptions(groundOptionsQuery),
    enabled: meta.showParentGround,
  });

  const userOptionsQueryResult = useQuery({
    queryKey: ["supply-basic-settings", "user-options", userOptionsQuery],
    queryFn: () => fetchUserOptions(userOptionsQuery),
    enabled: meta.showUserOptions,
  });

  const listPage = query.data ?? {
    items: [],
    page,
    pageSize,
    total: 0,
  };

  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: queryRootKey,
    });

  const createMutation = useMutation({
    mutationFn: (payload: SupplierMutation) => createSupplier(kind, payload),
    onSuccess: invalidateList,
  });
  const updateMutation = useMutation({
    mutationFn: ({
      supplierId,
      payload,
    }: {
      supplierId: string;
      payload: SupplierMutation;
    }) => updateSupplier(kind, supplierId, payload),
    onSuccess: invalidateList,
  });
  const disableMutation = useMutation({
    mutationFn: (supplierId: string) => disableSupplier(kind, supplierId),
    onSuccess: invalidateList,
  });
  const enableMutation = useMutation({
    mutationFn: (supplierId: string) => enableSupplier(kind, supplierId),
    onSuccess: invalidateList,
  });

  return {
    meta,
    draftFilters,
    items: listPage.items,
    loading: query.isLoading,
    page,
    pageSize,
    total: listPage.total,
    errorMessage: query.error
      ? isHttpClientError(query.error) && query.error.statusCode === 403
        ? "当前账号没有查看供应商列表的权限，请联系管理员开通。"
        : `${meta.title}列表加载失败，请稍后重试。`
      : "",
    mutationErrorMessage,
    housLevelOptions: housLevelQuery.data ?? [],
    groundOptions: groundQuery.data ?? [],
    userOptions: userOptionsQueryResult.data ?? [],
    searchUserOptions(query: string) {
      setUserOptionsQuery(query);
    },
    searchGroundOptions(query: string) {
      setGroundOptionsQuery(query);
    },
    setDraftFilters,
    clearMutationError,
    handleSearch() {
      setSubmittedFilters(normalizeSupplierFilters(draftFilters));
      setPage(1);
    },
    handleReset() {
      setDraftFilters({});
      setSubmittedFilters({});
      setPage(1);
    },
    handlePageChange(nextPage: number) {
      setPage(Math.max(nextPage, 1));
    },
    handlePageSizeChange(nextPageSize: number) {
      setPageSize(nextPageSize);
      setPage(1);
    },
    async handleCreate(payload: SupplierMutation) {
      await runMutation(() => createMutation.mutateAsync(payload), setMutationErrorMessage);
    },
    async handleUpdate(item: SupplierItem, payload: SupplierMutation) {
      await runMutation(
        () => updateMutation.mutateAsync({ supplierId: item.supplierId, payload }),
        setMutationErrorMessage,
      );
    },
    async handleDisable(item: SupplierItem) {
      await runMutation(() => disableMutation.mutateAsync(item.supplierId), setMutationErrorMessage);
    },
    async handleEnable(item: SupplierItem) {
      await runMutation(() => enableMutation.mutateAsync(item.supplierId), setMutationErrorMessage);
    },
  };
}

/** 执行供应商写操作并统一回填错误文案。 */
async function runMutation<T>(
  execute: () => Promise<T>,
  setMutationErrorMessage: (message: string) => void,
) {
  setMutationErrorMessage("");
  try {
    return await execute();
  } catch (error) {
    const code = isHttpClientError(error) ? error.code : null;
    const statusCode = isHttpClientError(error) ? error.statusCode : null;
    const message = isHttpClientError(error) ? error.message : undefined;
    setMutationErrorMessage(
      mapSupplierMutationError(
        code,
        "保存失败，请检查填写内容后重试。",
        message,
        statusCode,
      ),
    );
    throw error;
  }
}
